import { msToFullTime } from "@/utils/datetime.helper";

export const parseDebriefsAndStudents = (data) => {
  const debriefs = {};
  const students = {};

  for (const debrief of data) {
    const dateStr = new Date(debrief.createdAt).toDateString();

    if (!(dateStr in debriefs)) debriefs[dateStr] = [];

    debriefs[dateStr].push({
      ...debrief,
      debriefJson: JSON.parse(debrief.debriefJson),
    });

    const displayName = getDisplayNameFromName(debrief.student.name);
    const currentLetter = displayName[0].toUpperCase();

    if (!(currentLetter in students)) students[currentLetter] = [];

    if (!students[currentLetter].find((s) => s.id === debrief.student.id)) {
      students[currentLetter].push({
        ...debrief.student,
        name: displayName,
      });
    }
  }

  return { debriefs, students };
};

/**
 * @param fullName A name in the format "Firstname ...middlenames Lastname"
 * @returns {string} A name in the format "Lastname, Firstname ...middlenames"
 */
export const getDisplayNameFromName = (fullName) => {
  if (!validateName(fullName)) return "Unnamed";

  const splitNames = fullName.split(" ");

  if (splitNames.length === 1) return fullName;

  const startParts = getStartPartsOfName(splitNames);
  return splitNames[splitNames.length - 1] + ", " + startParts.join(" ");
};

/**
 * Gets array of ["First ...Middlenames", "Last"] names
 * @param fullName
 * @returns {string|(*)[]|*}
 */
export const getFirstAndLastNames = (fullName) => {
  if (!validateName(fullName)) return ["Unnamed"];

  const splitNames = fullName.split(" ");

  if (splitNames.length === 1) return [fullName];

  const startParts = getStartPartsOfName(splitNames);
  return [startParts.join(" "), splitNames[splitNames.length - 1]];
};

const validateName = (fullName) =>
  typeof fullName === "string" && fullName.length;

const getStartPartsOfName = (splitNamesArr) =>
  splitNamesArr.slice(0, splitNamesArr.length - 1);

export const buildDebriefEvent = (event) => {
  const prettyTime = msToFullTime(Math.round(event.actualTime) * 1000, true);

  const ret = {
    time: prettyTime,
    img: require("@/assets/tms-event-icons/" +
      getImageForDebriefEvent(event.eventType)),
    name: event.title,
  };

  if (
    event.eventType === EVENT_TYPES.EVENT_CHECKLIST_CHECK ||
    event.eventType === EVENT_TYPES.EVENT_CHECKLIST_GRADED
  ) {
    const components = event.title.split(":");
    let title = components[0];
    const value = components[components.length - 1];

    if (components.length > 2) {
      for (let i = 1; i < components.length - 1; i++) {
        title = title + ":" + components[i];
      }
    }

    ret.name = title;

    let checkIcon;
    if (+value === 1) checkIcon = "clear";
    else if (+value === 2) checkIcon = "check";

    ret.checkIcon = checkIcon;
  }

  return ret;
};

export const getImageForDebriefEvent = (eventType) => {
  switch (eventType) {
    case EVENT_TYPES.EVENT_SCENARIO_START:
    case EVENT_TYPES.EVENT_SCENARIO_RESUMED:
      return "play.png";
    case EVENT_TYPES.EVENT_SCENARIO_STOPPED:
      return "stop.png";
    case EVENT_TYPES.EVENT_SCENARIO_PAUSED:
      return "pause.png";
    case EVENT_TYPES.EVENT_FLAG_MARKER:
      return "flag.png";
    case EVENT_TYPES.EVENT_CAMERA_ON:
    case EVENT_TYPES.EVENT_CAMERA_OFF:
      return "surveillance-camera.png";
    case EVENT_TYPES.EVENT_CHECKLIST_GRADED:
    case EVENT_TYPES.EVENT_CHECKLIST_CHECK:
      return "exclamation.png";
    case EVENT_TYPES.EVENT_ENGAGE_POSITIVE:
      return "like-hand.png";
    case EVENT_TYPES.EVENT_ENGAGE_NEGATIVE:
      return "dislike-hand.png";
    case EVENT_TYPES.EVENT_ENGAGE_TEXT:
      return "speech-bubble.png";
    case EVENT_TYPES.EVENT_CUSTOM_IMAGE_SENT:
    case EVENT_TYPES.EVENT_BUILT_IN_IMAGE_SENT:
      return "picture.png";
    case EVENT_TYPES.EVENT_CUSTOM_VIDEO_SENT:
      return "movie-clapper.png";
    default:
      return "generic-event.png";
  }
};

const EVENT_TYPES = {
  EVENT_SCENARIO_START: 0,
  EVENT_SCENARIO_STOPPED: 1,
  EVENT_SCENARIO_PAUSED: 2,
  EVENT_SCENARIO_RESUMED: 3,

  EVENT_WAVEFORM_HIDDEN: 4,
  EVENT_WAVEFORM_UNHIDDEN: 5,
  EVENT_QUICKPICK_SELECTED: 6,

  EVENT_SCENARIO_MODE_CHANGED: 7,

  EVENT_CPR_CHANGED: 8,
  EVENT_PEA: 9,
  EVENT_TREND: 10,
  EVENT_TREND_VALUE: 11,

  EVENT_MODECHANGE: 12,

  EVENT_AED: 13,

  EVENT_DEFIB_CHARGE: 14,
  EVENT_DEFIB_SHOCK: 15,
  EVENT_DEFIB_DISARM: 16,

  EVENT_CHECKLIST_CHECK: 17,
  EVENT_CHECKLIST_GRADED: 18,

  EVENT_BLOODWORK: 19,
  EVENT_NIBP: 20,

  EVENT_12LEAD_SHOWN: 21,
  EVENT_12LEAD_HIDDEN: 22,
  EVENT_12LEAD_REQUESTED: 23,

  EVENT_FLAG_MARKER: 24,

  EVENT_CAMERA_ON: 25,
  EVENT_CAMERA_OFF: 26,
  EVENT_CAMERA_DISCONNECT: 27,

  EVENT_PACER: 28,
  EVENT_DEFIB: 29,
  EVENT_SYNC: 30,

  EVENT_ENGAGE_POSITIVE: 31,
  EVENT_ENGAGE_NEGATIVE: 32,
  EVENT_ENGAGE_TEXT: 33,

  EVENT_DEFIB_CHARGE_SCREEN_SHOWN: 34,
  EVENT_DEFIB_CHARGE_SCREEN_HIDDEN: 35,

  EVENT_LIFEPAK_EVENT: 36,
  EVENT_CORPULS3_EVENT: 37,
  EVENT_ZOLLR_EVENT: 38,
  EVENT_TOUCH7_EVENT: 39,
  EVENT_TEMPUSPRO_EVENT: 40,

  EVENT_BUILT_IN_IMAGE_SENT: 41,
  EVENT_CUSTOM_IMAGE_SENT: 42,
  EVENT_CUSTOM_VIDEO_SENT: 43,

  EVENT_LAB: 44,

  EVENT_TEXT_BOX: 45,
};
